import { NavLink, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchForPath } from '../lib/route-prefetch';

const TABS = [
  { to: '/today', label: 'Today', icon: 'book' },
  { to: '/memories', label: 'Memories', icon: 'stones' },
  { to: '/generations', label: 'Generations', icon: 'family-tree' },
  { to: '/walk', label: 'Grow', icon: 'walk' },
  { to: '/chat', label: 'Chat', icon: 'chat' },
] as const;

export function TabBar() {
  const { pathname } = useLocation();
  const qc = useQueryClient();
  // Warm the destination's queries the moment the user's finger lands.
  // Pointer-down fires ~80–150 ms before React Router commits the new
  // route, which is usually enough for the API call to land before
  // first paint. Mouse-enter covers desktop, where pointer-down doesn't
  // fire until click.
  const warm = (to: string) => () => prefetchForPath(qc, to);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-line"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px))' }}
      aria-label="Primary"
    >
      <div className="flex h-[120px] items-end px-1 pb-2">
        {TABS.map((t) => {
          const active = pathname.startsWith(t.to);
          return (
            <NavLink
              key={t.to}
              to={t.to}
              onPointerDown={warm(t.to)}
              onMouseEnter={warm(t.to)}
              onFocus={warm(t.to)}
              className={`flex-1 flex flex-col items-center justify-end gap-1 py-3 ${
                active ? 'text-oxblood' : 'text-muted'
              }`}
            >
              <span className="relative inline-block">
                <TabIcon kind={t.icon} className="w-7 h-7" />
              </span>
              <span className="text-[12px]">{t.label}</span>
              {active && <span className="w-1.5 h-1.5 rounded-full bg-oxblood mt-0.5" />}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

function TabIcon({ kind, className }: { kind: string; className?: string }) {
  // Hand-drawn line icons in SVG. Stroke uses currentColor so it picks
  // up the active oxblood / inactive muted from the parent.
  const stroke = 'currentColor';
  const sw = 1.7;
  switch (kind) {
    case 'book':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none">
          <path
            d="M3 5l9 2 9-2v14l-9-2-9 2V5z"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path d="M12 7v12" stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    case 'chat':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none">
          <path
            d="M4 5h16v11H8.5L4 19V5z"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'walk':
      // Christian cross — vertical post + horizontal crossbar (upper third).
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none">
          <path
            d="M12 3v18M6 9h12"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'family-tree':
      // Three little heads on a branching line — extended family.
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none">
          <circle cx="12" cy="5" r="2" stroke={stroke} strokeWidth={sw} />
          <circle cx="6" cy="18" r="2" stroke={stroke} strokeWidth={sw} />
          <circle cx="18" cy="18" r="2" stroke={stroke} strokeWidth={sw} />
          <path
            d="M12 7v3M12 10h-6v6M12 10h6v6"
            stroke={stroke}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'stones':
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none">
          <ellipse cx="12" cy="18" rx="9" ry="2.5" stroke={stroke} strokeWidth={sw} />
          <ellipse cx="12" cy="12" rx="7" ry="2.2" stroke={stroke} strokeWidth={sw} />
          <ellipse cx="12" cy="7" rx="5" ry="1.8" stroke={stroke} strokeWidth={sw} />
        </svg>
      );
    default:
      return null;
  }
}
